// Support-ticket API client (Story #4613, Streams 8a/8b/8c).
//
// Fetch-based (not the axios `api` instance) for two reasons: (1) the admin
// surface (/admin/support) lives in the same route context where react-query +
// axios reads were shown to hang (see hooks/useAuthedFetch.ts), and (2) it keeps
// one consistent client for both requester and admin calls. The auth header
// mirrors the axios request interceptor (Bearer id-token from localStorage).
//
// Every path here matches the deployed lambda/innovateflow-api/support/handler.py
// exactly. The base is `${API_ENDPOINT}/innovateflow` + `/support`.

import { STORAGE_KEYS } from '../constants/config'
import type {
  AdminSupportTicket,
  CloseReason,
  RequestType,
  StageResponse,
  SupportTicket,
} from '../types/support'

function apiBase(): string {
  const ep = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG?.API_ENDPOINT : undefined
  return ep ? `${ep}/innovateflow` : ''
}

function authHeaders(json = true): Record<string, string> {
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/** Thrown on an optimistic-lock 409 so admin relation mutations can refresh+retry. */
export class ConflictError extends Error {
  readonly status = 409
  constructor(message: string) {
    super(message)
    this.name = 'ConflictError'
  }
}

export class SupportApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'SupportApiError'
    this.status = status
  }
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json()
    return (body?.error as string) || (body?.message as string) || `HTTP ${res.status}`
  } catch {
    return `HTTP ${res.status}`
  }
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${apiBase()}/support${path}`, {
    method,
    headers: authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })
  if (!res.ok) {
    const message = await parseError(res)
    if (res.status === 409) throw new ConflictError(message)
    throw new SupportApiError(message, res.status)
  }
  // 204 / empty bodies are valid for some mutations.
  const text = await res.text()
  return (text ? JSON.parse(text) : {}) as T
}

// ── Requester endpoints ─────────────────────────────────────────────────────

export interface CreateTicketInput {
  subject: string
  description: string
  request_type: RequestType
  portal: string
  idempotency_key: string
  /** Extra fields the mockup collects; the current backend ignores unknown keys. */
  priority?: string
  diagnostic_context?: Record<string, unknown>
}

export function createTicket(input: CreateTicketInput): Promise<SupportTicket> {
  return request<SupportTicket>('POST', '/tickets', input)
}

export function listMyTickets(signal?: AbortSignal): Promise<{ tickets: SupportTicket[] }> {
  return request<{ tickets: SupportTicket[] }>('GET', '/tickets', undefined, signal)
}

export function getTicket(id: string, signal?: AbortSignal): Promise<SupportTicket> {
  return request<SupportTicket>('GET', `/tickets/${id}`, undefined, signal)
}

export function replyToTicket(id: string, message: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('POST', `/tickets/${id}/reply`, { message })
}

export function closeTicket(id: string, close_reason: CloseReason): Promise<{ ok: boolean; status: string }> {
  return request('POST', `/tickets/${id}/close`, { close_reason })
}

export function reopenTicket(id: string): Promise<{ ok: boolean; status: string }> {
  return request('POST', `/tickets/${id}/reopen`, {})
}

export function stageAttachment(
  ticketId: string,
  meta: { filename: string; content_type: string; size_bytes: number },
): Promise<StageResponse> {
  return request<StageResponse>('POST', `/tickets/${ticketId}/attachments/stage`, meta)
}

/**
 * Upload the raw file bytes to the presigned S3 POST returned by stage. This is a
 * cross-origin multipart POST to S3 directly — NO Authorization header, and the
 * presigned `fields` MUST be appended before the file part (S3 policy order).
 */
export async function uploadToPresignedPost(
  upload: StageResponse['upload'],
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const form = new FormData()
  Object.entries(upload.fields).forEach(([k, v]) => form.append(k, v))
  form.append('file', file)

  // XHR (not fetch) so we get real upload progress events.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', upload.url)
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total)
      }
    }
    xhr.onload = () => {
      // S3 returns 204 (or 201 when success_action_status set) on success.
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new SupportApiError(`Upload failed (${xhr.status})`, xhr.status))
    }
    xhr.onerror = () => reject(new SupportApiError('Upload failed — network error', 0))
    xhr.send(form)
  })
}

export function bindAttachment(
  ticketId: string,
  attachmentId: string,
): Promise<{ attachment_id: string; bound?: boolean; already_bound?: boolean }> {
  return request('POST', `/tickets/${ticketId}/attachments/${attachmentId}/bind`, {})
}

export function deleteAttachment(ticketId: string, attachmentId: string): Promise<{ ok: boolean }> {
  return request('DELETE', `/tickets/${ticketId}/attachments/${attachmentId}`)
}

// ── Admin endpoints ─────────────────────────────────────────────────────────

export function adminListTickets(
  params: { portal?: string; status?: 'open' | 'closed' },
  signal?: AbortSignal,
): Promise<{ tickets: AdminSupportTicket[] }> {
  const qs = new URLSearchParams()
  if (params.portal) qs.set('portal', params.portal)
  if (params.status) qs.set('status', params.status)
  const q = qs.toString()
  return request<{ tickets: AdminSupportTicket[] }>('GET', `/admin/tickets${q ? `?${q}` : ''}`, undefined, signal)
}

export function adminGetTicket(id: string, signal?: AbortSignal): Promise<AdminSupportTicket> {
  return request<AdminSupportTicket>('GET', `/admin/tickets/${id}`, undefined, signal)
}

export function escalateTicket(id: string): Promise<{ ok: boolean; escalated: boolean; sms?: string }> {
  return request('POST', `/admin/tickets/${id}/escalate`, {})
}

export function adminReply(
  id: string,
  message: string,
  visibility: 'public' | 'internal',
): Promise<{ ok: boolean; visibility: string }> {
  return request('POST', `/admin/tickets/${id}/reply`, { message, visibility })
}

export function markDuplicateOf(
  id: string,
  primary_id: string,
  version: number,
): Promise<{ ok: boolean; duplicate_of: string }> {
  return request('POST', `/admin/tickets/${id}/duplicate-of`, { primary_id, version })
}

export function linkTicket(id: string, other_id: string, version: number): Promise<{ ok: boolean; other_id: string }> {
  return request('POST', `/admin/tickets/${id}/link`, { other_id, version })
}

export function unlinkTicket(id: string, other_id: string, version: number): Promise<{ ok: boolean; other_id: string }> {
  return request('DELETE', `/admin/tickets/${id}/link`, { other_id, version })
}

export function undoDuplicate(id: string, version: number): Promise<{ ok: boolean; status: string }> {
  return request('POST', `/admin/tickets/${id}/undo-duplicate`, { version })
}

export function retrySync(id: string, outbox_id: string): Promise<{ ok: boolean; status: string }> {
  return request('POST', `/admin/tickets/${id}/retry-sync`, { outbox_id })
}

export function releaseQuarantine(
  id: string,
  attachmentId: string,
  justification: string,
): Promise<{ ok: boolean; released: boolean | string }> {
  return request('POST', `/admin/tickets/${id}/attachments/${attachmentId}/release-quarantine`, { justification })
}

export function discardAttachment(id: string, attachmentId: string): Promise<{ ok: boolean; discarded: boolean }> {
  return request('POST', `/admin/tickets/${id}/attachments/${attachmentId}/discard`, {})
}
